import { TableComponent } from '../../ui/TableComponent';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { TopSection } from '../../ui/TopSection';
import { useOrderHistory, OrderHistory } from '../../../stores/appOrderHistoryStore';
import { useAppUserDefaultStore, saveVatOrdersSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterOrdersByDateRange } from '../../../utils/dateRangeFilters';
import { getAvailablePlatforms, filterOrdersByOverallType } from '../../orderhistory/utils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoadOrderHistory } from '../../../utils/dataLoadingUtils';
import { StatCard } from '../../ui/StatCard';
import { iconBgAmber, iconBgBlue, iconBgGreen, primaryText, regularText, smallTextSize, tertiaryText, semiBoldText } from '../../../styling/stylingHelper';
import { Percent, ShoppingCart, Wallet } from 'lucide-react';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { currencyFormatter } from '../../../services/helperService';
import { cn } from '../../../lib/utils';
import { metricIcon } from '../../ui/metricIcons';
import { getTableFormattedDate, toDate } from '../../../utils/dateUtils';
import { useOrderFilters } from '../../../hooks/useOrderFilters';
import { filterAllOrders } from '../../../utils/filterUtils';
import { resetScrollToTop, restoreScrollPosition, saveCurrentScrollPosition, saveTablePage, getTablePage, clearTablePage } from '../../../utils/scrollUtils';
import { isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useTranslation } from 'react-i18next';
import { exportOrdersToExcel } from '../../../services/orderhistory/excelExportService';
import { buildOrderLineItemsClipboardText } from '../../../services/orderhistory/orderLineItemsExport';
import { OrderActionsMenu } from './OrderActionsMenu';
import { format, parseISO } from 'date-fns';
import { Trash2, AlertTriangle, ExternalLink, FileX } from 'lucide-react';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import { deleteOrder } from '../../../services/orderhistory/orderhistoryService';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessInvoicesPage, hasDashboardFoodOnly } from '../../../services/accessManagement/accessManagement';
import { calculateProductTotal } from '../../../utils/platformUtils';
import { getOrderTotal, getOrderVatAmount, getOrderVatRate, VatRateBadges } from '../../orderhistory/vatUtils';

import { SegmentedToggle } from '../../ui/SegmentedToggle';
import { Factuur, fetchFacturenByPlatformIds } from '../../../services/orderhistory/factuurService';
import { factuurOrdersForFiltering, regroupFacturen, FactuurTableRow } from '../../../utils/factuurUtils';

// Elevated card surface for the mobile card list (matches the shipped OrderHistoryPage cards).
const MOBILE_CARD =
  'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

// How many mobile cards to reveal per "Load more" tap (never render the whole list at once).
const MOBILE_BATCH = 20;

/**
 * Combined ordering account under a food/drinks filter: this invoice appears in BOTH the food and
 * the drinks view, each time with only its own side's lines, so its total here is a PART of the
 * invoice. The badge is the only warning the reader gets that the number isn't the invoice total.
 * Spec: z_ralph/tasks/1_new_tasks/spec-combined-ordering-accounts.md §9
 */
const SplitByCategoryBadge = ({ label, title }: { label: string; title: string }) => (
  <span
    title={title}
    className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
  >
    {label}
  </span>
);

const OrderHistoryOrdersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFacturen = searchParams.get('view') === 'facturen';
  const [facturen, setFacturen] = useState<Factuur[]>([]);
  const [facturenLoading, setFacturenLoading] = useState(false);
  const [facturenError, setFacturenError] = useState(false);
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();

  // Get search query from store (persisted across navigation)
  const searchQuery = useFiltersStore(s => s.orderSearchQuery);
  const setSearchQuery = useFiltersStore(s => s.setOrderSearchQuery);

  // Local state for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<{ id: string; orderNumber: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get saved table page number when component mounts (for restoration after navigation)
  const [initialTablePage, setInitialTablePage] = useState(() => getTablePage());

  // Mobile-only: how many order cards are rendered (grows on "Load more").
  // The desktop table paginates internally; on mobile we window the same array.
  const [mobileVisible, setMobileVisible] = useState<number>(MOBILE_BATCH);

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Restore scroll position when returning from detail page
  useEffect(() => {
    restoreScrollPosition();
  }, []);

  // Clear table page when component unmounts (navigating away from this page entirely)
  useEffect(() => {
    return () => {
      // Only clear if we're not just navigating to a detail page
      // (The detail page navigation is handled in handleRowClick)
      const isNavigatingToDetail = window.location.pathname.includes('/orderhistory/');
      if (!isNavigatingToDetail) {
        clearTablePage();
      }
    };
  }, []);

  // Connect to stores
  const orderHistoryStore = useOrderHistory();
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const platformMap = useRestaurantStore(s => s.restaurantSectionPlatforms);
  const restaurantDataLoaded = useRestaurantStore(s => s.isDataLoaded);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    const availableOptions = ['all', 'food', 'drinks', 'non-food'];
    if (overallTypeFilter && !availableOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Order filters hook - only include platform filters for this page
  const {
    filters,
    setPlatformFilters,
    setSectionFilters,
    resetFilters,
    activeFilterCount,
  } = useOrderFilters({
    includeRestaurants: isAllRestaurants || false,
    includePlatforms: true,
    includeSections: true,
    includeCategories: false,
  });

  // Time controls - centralized logic. Declared before the data loading hook so the
  // progressive loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load order history progressively: the selected window (default last week) renders
  // first, then the rest of the history streams in the background.
  useLoadOrderHistory({ progressive: true, viewStart: startDate });

  const factuurPlatformIds = useMemo(() => {
    const sections = isAllRestaurants ? Object.values(restaurantSectionsMap).flat()
      : restaurantId ? restaurantSectionsMap[restaurantId] || [] : [];
    return Array.from(new Set(sections.flatMap(section => (platformMap[section.id] || []).map(platform => platform.id)))).sort();
  }, [isAllRestaurants, restaurantId, restaurantSectionsMap, platformMap]);
  const factuurStartDate = format(startDate, 'yyyy-MM-dd');
  const factuurEndDate = format(endDate, 'yyyy-MM-dd');

  useEffect(() => {
    if (!isFacturen) return;
    const controller = new AbortController();
    setFacturen([]);
    setFacturenError(false);
    if (!restaurantDataLoaded || !restaurantId) {
      setFacturenLoading(true);
      return () => controller.abort();
    }
    if (!factuurPlatformIds.length) {
      setFacturenLoading(false);
      return () => controller.abort();
    }
    setFacturenLoading(true);
    fetchFacturenByPlatformIds(factuurPlatformIds, parseISO(factuurStartDate), parseISO(factuurEndDate), controller.signal)
      .then(data => { if (!controller.signal.aborted) setFacturen(data); })
      .catch(error => {
        if (!controller.signal.aborted) {
          console.error('Failed to load invoices:', error);
          setFacturenError(true);
        }
      })
      .finally(() => { if (!controller.signal.aborted) setFacturenLoading(false); });
    return () => controller.abort();
  }, [isFacturen, restaurantDataLoaded, restaurantId, factuurPlatformIds, factuurStartDate, factuurEndDate]);

  const invoiceOrders = useMemo(() => facturen.flatMap(factuur => factuurOrdersForFiltering(factuur, platformToSectionIndex)),
    [facturen, platformToSectionIndex]);

  // Progressive-loading state: "loading" means the currently viewed period hasn't fully
  // streamed in yet. The all-time view can only be trusted once everything is loaded.
  const ordersFirstChunkLoaded = useOrderHistory(s => s.firstChunkLoaded);
  const ordersFullyLoaded = useOrderHistory(s => s.fullyLoaded);
  const ordersLoadedFromDate = useOrderHistory(s => s.loadedFromDate);
  const isOrdersLoading =
    !ordersFullyLoaded && (
      !ordersFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!ordersLoadedFromDate && startDate < ordersLoadedFromDate)
    );

  // Filter data by date range
  const dateFilteredOrders = useMemo(() =>
    filterOrdersByDateRange(orderHistoryStore.orderHistoryList, startDate, endDate),
    [orderHistoryStore.orderHistoryList, startDate, endDate]
  );

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks' | 'non-food');
  };

  // Handler for VAT toggle - updates orders VAT setting
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await saveVatOrdersSetting(showExVat);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Apply overall type filter, then all order filters (restaurants, sections, platforms)
  const overallTypeFilteredOrders = useMemo(() =>
    filterOrdersByOverallType(isFacturen ? invoiceOrders : dateFilteredOrders, overallTypeFilter),
    [isFacturen, invoiceOrders, dateFilteredOrders, overallTypeFilter]
  );

  const filteredOrders = useMemo(() =>
    filterAllOrders(
      overallTypeFilteredOrders,
      filters,
      isAllRestaurants ? restaurantFilters : undefined,
      platformToSectionIndex,
      isFacturen ? filters.subCategories : undefined
    ),
    [overallTypeFilteredOrders, filters, isFacturen, isAllRestaurants, restaurantFilters, platformToSectionIndex]
  );

  // Show debug info only for admin users with dashboard_food_only setting
  const showDebugInfo = backendUser?.role === 'admin' && hasDashboardFoodOnly(backendUser, accountSettings);

  // Restaurant id → name, for the "All Restaurants" restaurant column.
  const restaurantNameById = useMemo(() =>
    new Map(restaurants.map(r => [r.id, r.name])),
    [restaurants]
  );

  // Which restaurant an order belongs to — only shown in the "All Restaurants" view.
  // The enriched section is the authority; the order's own fields are the fallback.
  const restaurantNameOf = useCallback((order: OrderHistory) => {
    const orderRestaurantId = order.restaurantSection?.restaurantId || order.restaurantId;
    return (orderRestaurantId && restaurantNameById.get(orderRestaurantId)) || order.restaurantName || '--';
  }, [restaurantNameById]);

  // Format orders for the table. Every money figure comes from the vatUtils helpers — the naive
  // `totalWithTaxes - total` is wrong for the shipping-included platforms, and `totalWithTaxes`
  // is nullable, so it always needs the ex-VAT fallback `getOrderTotal` applies.
  const tableData = useMemo<FactuurTableRow[]>(() => {
    if (isFacturen) return regroupFacturen(facturen, filteredOrders, overallTypeFilter !== 'all' || filters.categories.length > 0 || filters.subCategories.length > 0, restaurantNameById);
    return filteredOrders.map(order => {
      // Calculate product sum for mismatch detection (always ex-VAT to compare with order.total)
      let totalMismatch = false;
      if (showDebugInfo && order.productData?.length) {
        const productSum = order.productData.reduce(
          (sum, product) => sum + calculateProductTotal(product, order.platform), 0
        );
        const orderTotalExVat = order.total || 0;
        const shipping = order.shipping || 0;
        totalMismatch = Math.abs(orderTotalExVat - productSum) > 1 && Math.abs(orderTotalExVat - (productSum + shipping)) > 1;
      }

      // No `totalWithTaxes` means the invoice carried no tax data at all: VAT reads 0 and the
      // incl-VAT total falls back to the ex-VAT one, so the three columns stay consistent.
      const hasTaxData = order.totalWithTaxes !== undefined;
      const vatAmount = hasTaxData
        ? getOrderVatAmount(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0)
        : 0;

      // Prefer the rates the supplier charged per line; only a line-less order falls back to the
      // order-level (blended) rate.
      const lineVatRates = (order.productData || []).map(p => p.taxRate).filter(rate => rate !== undefined);
      const vatRates = lineVatRates.length > 0
        ? lineVatRates
        : [order.taxRate ?? (hasTaxData
            ? getOrderVatRate(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0, order.productData)
            : undefined)];

      const restaurantName = isAllRestaurants ? restaurantNameOf(order) : '';

      return {
        orderNumber: order.orderNumber || '',
        orderNumbers: '',
        orderCount: 0,
        restaurantName,
        platform: order.platform,
        unknownSupplierName: order.unknownSupplierName,
        clientName: order.clientName || '-',
        orderDate: getTableFormattedDate(toDate(order.orderDate)),
        totalExVat: getOrderTotal(order, true),
        vatAmount,
        totalInclVat: getOrderTotal(order, false),
        vatRates,
        taxCalculationMethod: order.taxCalculationMethod,
        // The order's own PDF, or the linked factuur's when the order itself came from an email body.
        fileUrl: order.sourceFileUrl ?? order.factuurSourceFileUrl,
        totalMismatch,
        id: order.id,
        // One combined account's invoice is shown split across the food and drinks views, so its
        // row total here is only that side's lines. Never under 'all' — the order is whole there.
        splitByCategory:
          order.restaurantSection?.combinedOrderingAccount === true &&
          (overallTypeFilter === 'food' || overallTypeFilter === 'drinks'),
        // Concatenated product names so orders can be searched by the products they contain
        productNames: (order.productData || []).map(p => p.name).join(' ').toLowerCase(),
      };
    });
  }, [isFacturen, facturen, filteredOrders, filters.categories.length, filters.subCategories.length, restaurantNameById, showDebugInfo, isAllRestaurants, restaurantNameOf, overallTypeFilter]);

  // Apply search query filter
  const searchFilteredTableData = useMemo(() => {
    if (!searchQuery.trim()) return tableData;

    const query = searchQuery.toLowerCase().trim();
    return tableData.filter(order =>
      order.orderNumber?.toLowerCase().includes(query) ||
      order.orderNumbers.includes(query) ||
      order.platform?.toLowerCase().includes(query) ||
      order.unknownSupplierName?.toLowerCase().includes(query) ||
      order.productNames?.includes(query)
    );
  }, [tableData, searchQuery]);

  // Reset the mobile window whenever the filtered result set changes.
  useEffect(() => {
    setMobileVisible(MOBILE_BATCH);
  }, [searchFilteredTableData]);

  // KPIs over exactly the rows the table shows (date + type + panel filters AND the search box),
  // summed from the same per-row figures so a card can never disagree with its column.
  const kpis = useMemo(() => {
    let totalExVat = 0;
    let totalVat = 0;
    let totalInclVat = 0;

    for (const order of searchFilteredTableData) {
      totalExVat += order.totalExVat || 0;
      totalVat += order.vatAmount || 0;
      totalInclVat += order.totalInclVat || 0;
    }

    return {
      totalExVat,
      totalVat,
      totalInclVat,
      orderCount: searchFilteredTableData.length,
    };
  }, [searchFilteredTableData]);

  const handleGoBack = () => {
    navigate("/orderhistory");
  };

  // Format platform options for the filter
  const formatPlatformName = (platform: string) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const formatPlatformOptions = (platforms: string[]) => {
    return platforms.map(p => ({ id: p, name: formatPlatformName(p) }));
  };

  const handleRowClick = (row: any) => {
    // Save scroll position before navigating to detail page
    saveCurrentScrollPosition();
    navigate(isFacturen ? `/orderhistory/factuur/${row.id}` : `/orderhistory/${row.id}`);
  };

  // Handler for table page changes
  const handleTablePageChange = (page: number) => {
    saveTablePage(page);
  };

  // Handler for Excel export
  const handleExport = () => {
    // Generate filename with date range
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');
    const filename = `order-history_${startDateStr}_to_${endDateStr}`;

    // Export the filtered orders (not the search filtered table data, but the actual filtered orders)
    exportOrdersToExcel(filteredOrders, showExVatOrders, filename);
  };

  // Handler for the clipboard export: the same line items the Excel file's "Product Details"
  // sheet carries, as tab-separated text. Same scope as the Excel export — the panel filters and
  // the VAT toggle, but deliberately NOT the table search box.
  const handleCopyLineItems = async (): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(
        buildOrderLineItemsClipboardText(
          filteredOrders,
          showExVatOrders,
          {
            productName: t('orderHistory.export.productName'),
            articleId: t('orderHistory.export.articleId'),
            supplier: t('orderHistory.supplierTableHeader'),
            restaurant: t('dashboard.restaurant'),
            category: t('orderHistory.category'),
            quantity: t('orderHistory.quantity'),
            unitPrice: t('orderHistory.unitPrice'),
            totalPrice: t('orderHistory.totalPrice'),
            orderNumber: t('orderHistory.orderNumber'),
            date: t('orderHistory.date'),
          },
          // Across restaurants the supplier alone can't attribute a line, so the column is added.
          { includeRestaurant: !!isAllRestaurants, restaurantNameOf }
        )
      );
      return true;
    } catch (error) {
      // Insecure context, denied permission or an unfocused document: no confirmation, no alert.
      console.error('Failed to copy order line items to clipboard:', error);
      return false;
    }
  };

  // Handler for delete button click
  const handleDeleteClick = (e: React.MouseEvent, orderId: string, orderNumber: string) => {
    e.stopPropagation(); // Prevent row click
    setOrderToDelete({ id: orderId, orderNumber });
    setIsDeleteModalOpen(true);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!orderToDelete) return;

    setIsDeleting(true);
    try {
      await deleteOrder(orderToDelete.id);

      // Remove the order from the store
      orderHistoryStore.removeOrder(orderToDelete.id);

      // Close the modal
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
    } catch (error) {
      console.error('Failed to delete order:', error);
      alert('Failed to delete order. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setOrderToDelete(null);
  };

  // Check if user has permission to delete orders (admin with invoices_page setting)
  const canDeleteOrders = backendUser && backendUser.role === 'admin'
    ? canAccessInvoicesPage(backendUser, accountSettings)
    : false;

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available sections from the restaurant store
  const availableSections = useMemo(() => {
    if (!restaurantId || !restaurantSectionsMap[restaurantId]) return [];
    return restaurantSectionsMap[restaurantId].map(s => ({ id: s.id, name: s.name }));
  }, [restaurantId, restaurantSectionsMap]);

  // Get available filter options from the overall-type-filtered data (before applying order filters)
  const availablePlatforms = useMemo(() =>
    getAvailablePlatforms(overallTypeFilteredOrders),
    [overallTypeFilteredOrders]
  );

  // Only enable export functionality if there is at least one order in filtered data
  const canExport = !isFacturen && filteredOrders.length > 0;
  const isLoading = isFacturen ? facturenLoading : isOrdersLoading;
  const pageTitle = t(isFacturen ? 'orderHistory.facturen.invoices' : 'orderHistory.orders');
  const searchPlaceholder = t(isFacturen ? 'orderHistory.facturen.search' : 'orderHistory.searchByOrderNumber');
  const changeView = (value: 'pakbonnen' | 'facturen') => {
    const next = new URLSearchParams(searchParams);
    if (value === 'facturen') next.set('view', 'facturen');
    else next.delete('view');
    clearTablePage();
    setInitialTablePage(1);
    setSearchParams(next);
  };
  const viewToggle = (fullWidth = false) => (
    <SegmentedToggle
      value={isFacturen ? 'facturen' : 'pakbonnen'}
      onChange={changeView}
      label={t('orderHistory.facturen.view')}
      options={[{ value: 'pakbonnen', label: t('orderHistory.facturen.packingSlips') }, { value: 'facturen', label: t('orderHistory.facturen.invoices') }]}
      fullWidth={fullWidth}
    />
  );

  return (
    <div>
      <MobilePageHeader title={pageTitle} right={<RestaurantSelector />} />
      <TopSection
        title={pageTitle}
        backButton={true}
        handleGoBack={handleGoBack}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        onOverallTypeChange={handleOverallTypeChange}
        currentOverallTypeValue={overallTypeFilter}
        overallTypeOptions={['all', 'food', 'drinks', 'non-food']}
        onVatChange={handleVatChange}
        currentVatValue={showExVatOrders ? 'excl' : 'incl'}
        showFilters={true}
        filters={{
          orderFilters: {
            restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
            setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
            availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
            platformFilters: filters.platforms,
            setPlatformFilters,
            availablePlatforms,
            sectionFilters: filters.sections,
            setSectionFilters,
            availableSections,
            onResetFilters: resetFilters,
            activeFilterCount,
          }
        }}
      />

      {isFacturen && facturenError && (
        <p role="alert" className="mb-4 text-red-600 dark:text-textRed-dark">{t('orderHistory.facturen.loadError')}</p>
      )}

      {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI block: four primary cards plus a quieter strip of supporting numbers */}
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* 2x2 on phones, one row from 1250px */}
          <div className="grid grid-cols-2 gap-3 sm:gap-6 min-[1250px]:grid-cols-4">
            <StatCard
              {...metricIcon('spend')}
              title={t('orderHistory.totalExclVat', { defaultValue: 'Total Excl. VAT' })}
              value={kpis.totalExVat}
              valueType='currency'
              loading={isLoading}
              mobileLayout="tile"
            />
            <StatCard
              icon={<Percent className="w-6 h-6" style={{ color: '#F59E0B' }} />}
              iconBg={iconBgAmber}
              title={t('orderHistory.totalVat', { defaultValue: 'Total VAT' })}
              value={kpis.totalVat}
              valueType='currency'
              loading={isLoading}
              mobileLayout="tile"
            />
            <StatCard
              icon={<Wallet className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title={t('orderHistory.totalInclVat', { defaultValue: 'Total Incl. VAT' })}
              value={kpis.totalInclVat}
              valueType='currency'
              loading={isLoading}
              mobileLayout="tile"
            />
            <StatCard
              icon={<ShoppingCart className="w-6 h-6" style={{ color: '#2E90FA' }} />}
              iconBg={iconBgBlue}
              title={t(isFacturen ? 'orderHistory.facturen.count' : 'orderHistory.ordersCount')}
              value={kpis.orderCount}
              valueType='number'
              loading={isLoading}
              mobileLayout="tile"
            />
          </div>
        </div>

        {/* Responsive layout group for content sections below 1250px */}
        <div className="flex flex-col gap-6 min-[1250px]:flex-col">
          {/* Orders table component */}
          <div className="w-full">
            <ResponsiveTable
              desktop={
            <TableComponent
              key={isFacturen ? 'facturen' : 'pakbonnen'}
              title={pageTitle}
              data={searchFilteredTableData}
              blankLines={false}
              usePagination={true}
              onRowClick={handleRowClick}
              firstColumn={'narrow'}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder={searchPlaceholder}
              headerActions={viewToggle()}
              headerActionsRight={canExport ? (
                <OrderActionsMenu onCopyLineItems={handleCopyLineItems} />
              ) : undefined}
              onExport={canExport ? handleExport : undefined}
              exportButtonText={canExport ? t('orderHistory.export.exportButton') : undefined}
              initialPage={initialTablePage || undefined}
              onPageChange={handleTablePageChange}
              columns={[
                { key: 'orderDate', label: t(isFacturen ? 'orderHistory.facturen.date' : 'orderHistory.date'), columnType: 'base' },
                // Restaurant column only makes sense across restaurants.
                ...(isAllRestaurants ? [{ key: 'restaurantName' as const, label: t('dashboard.restaurant'), columnType: 'name' as const }] : []),
                {
                  key: 'platform',
                  label: t('orderHistory.supplierTableHeader'),
                  columnType: 'platform',
                  filter: {
                    selectedValues: filters.platforms,
                    onValuesChange: setPlatformFilters,
                    availableOptions: formatPlatformOptions(availablePlatforms),
                    valueType: 'default',
                    filterLabel: 'Suppliers',
                  }
                },
                {
                  key: 'orderNumber',
                  label: t(isFacturen ? 'orderHistory.facturen.number' : 'orderHistory.orderNumberTableHeader'),
                  columnType: 'base',
                  // Same span the 'base' column type renders (TableValueComponent defaultReturn), so
                  // the cell is unchanged when there is no badge.
                  render: (value: any, row: any) => (
                    <div className="flex items-center gap-1.5">
                      <span className={cn(tertiaryText, smallTextSize, regularText)}>{String(value)}</span>
                      {row.splitByCategory && (
                        <SplitByCategoryBadge
                          label={t('orderHistory.splitByCategory')}
                          title={t('orderHistory.splitByCategoryTitle')}
                        />
                      )}
                    </div>
                  )
                },
                ...(isFacturen ? [{ key: 'orderCount' as const, label: t('orderHistory.facturen.ordersCount'), columnType: 'base' as const }] : []),
                {
                  key: 'totalExVat',
                  label: t('orderHistory.exclVat'),
                  columnType: 'currency',
                  ...(!isFacturen && showDebugInfo ? {
                    render: (_value: any, row: any) => (
                      <div className="flex items-center gap-1 justify-end">
                        <span className={cn(tertiaryText, smallTextSize)}>{currencyFormatter.format(row.totalExVat || 0)}</span>
                        {row.totalMismatch && (
                          <span title="Product sum does not match order total">
                            <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-darkOrangeColor-dark" />
                          </span>
                        )}
                      </div>
                    )
                  } : {})
                },
                {
                  key: 'vatRates',
                  label: t('orderHistory.vatRate'),
                  columnType: 'base',
                  render: (_value: any, row: any) => (
                    <VatRateBadges rates={row.vatRates} taxCalculationMethod={row.taxCalculationMethod} />
                  )
                },
                { key: 'vatAmount', label: t('orderHistory.vat', { defaultValue: 'VAT' }), columnType: 'currency' },
                { key: 'totalInclVat', label: t('orderHistory.inclVat'), columnType: 'currencyHighlightBlack' },
                {
                  key: 'fileUrl',
                  label: t('orderHistory.file', { defaultValue: 'File' }),
                  columnType: 'base',
                  render: (_value: any, row: any) => (
                    row.fileUrl ? (
                      <div className="flex justify-center">
                        <a
                          href={row.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {t('orderHistory.viewFile')}
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ) : (
                      <div
                        className="flex justify-center"
                        title={t('orderHistory.noFile', { defaultValue: 'No file' })}
                      >
                        <FileX className="w-4 h-4 opacity-30 text-textColor dark:text-textColor-dark" />
                      </div>
                    )
                  )
                },
                ...(!isFacturen && canDeleteOrders ? [{
                  key: 'actions' as any,
                  label: '',
                  columnType: 'base' as const,
                  render: (_value: any, row: any) => (
                    <button
                      onClick={(e) => handleDeleteClick(e, row.id, row.orderNumber)}
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors"
                      title="Delete order"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-textRed-dark" />
                    </button>
                  )
                }] : [])
              ]}
            />
              }
              mobile={
                <div className="w-full">
                  <h3 className={cn('mb-4 text-[18px] leading-6', semiBoldText, primaryText)}>{pageTitle}</h3>
                  <div className="mb-3">{viewToggle(true)}</div>
                  {/* Search — same state the desktop TableComponent search uses */}
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className={cn('w-full h-10 px-3 mb-3 border rounded-lg text-[14px] bg-white dark:bg-card-dark border-[#D0D5DD] dark:border-borderPrimary-dark', primaryText)}
                  />
                  <MobileCardList
                    items={searchFilteredTableData.slice(0, mobileVisible)}
                    keyOf={(row, i) => `${row.id ?? i}`}
                    onLoadMore={() => setMobileVisible((v) => v + MOBILE_BATCH)}
                    hasMore={mobileVisible < searchFilteredTableData.length}
                    loadMoreLabel={t('orderHistory.loadMore')}
                    renderCard={(row) => (
                      <div
                        className={cn(MOBILE_CARD, 'flex flex-col gap-3 cursor-pointer')}
                        onClick={() => handleRowClick(row)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <SupplierLogo platform={row.platform} unknownSupplierName={row.unknownSupplierName} size="medium" />
                            {row.splitByCategory && (
                              <SplitByCategoryBadge
                                label={t('orderHistory.splitByCategory')}
                                title={t('orderHistory.splitByCategoryTitle')}
                              />
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn('text-xs font-medium', tertiaryText)}>{t(isFacturen && showExVatOrders ? 'orderHistory.exclVat' : 'orderHistory.inclVat')}</span>
                            <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format((isFacturen && showExVatOrders ? row.totalExVat : row.totalInclVat) || 0)}</span>
                          </div>
                        </div>
                        <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                          {isAllRestaurants && (
                            <MobileCardField label={t('dashboard.restaurant')} value={row.restaurantName} divider />
                          )}
                          <MobileCardField label={t(isFacturen ? 'orderHistory.facturen.number' : 'orderHistory.orderNumber')} value={row.orderNumber || '-'} divider />
                          <MobileCardField label={t(isFacturen ? 'orderHistory.facturen.date' : 'orderHistory.date')} value={row.orderDate} />
                        </div>
                        {isFacturen && <MobileCardField label={t('orderHistory.facturen.ordersCount')} value={String(row.orderCount)} />}
                        {/* VAT breakdown — no dividers here: three fields plus the file button
                            only fit across a 320px card on gaps alone. */}
                        <div className="flex items-center justify-between gap-3 border-t border-border dark:border-border-dark pt-3">
                          <MobileCardField
                            label={t('orderHistory.exclVat')}
                            value={currencyFormatter.format(row.totalExVat || 0)}
                          />
                          <MobileCardField
                            label={t('orderHistory.vat', { defaultValue: 'VAT' })}
                            value={currencyFormatter.format(row.vatAmount || 0)}
                          />
                          <MobileCardField
                            label={t('orderHistory.vatRate')}
                            value={<VatRateBadges rates={row.vatRates} taxCalculationMethod={row.taxCalculationMethod} />}
                          />
                          {row.fileUrl ? (
                            <a
                              href={row.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 flex-shrink-0 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {t('orderHistory.viewFile')}
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          ) : (
                            <div
                              className="flex-shrink-0 p-2"
                              title={t('orderHistory.noFile', { defaultValue: 'No file' })}
                            >
                              <FileX className="w-4 h-4 opacity-30 text-textColor dark:text-textColor-dark" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Order"
        message={`Are you sure you want to delete order ${orderToDelete?.orderNumber}? This action cannot be undone.`}
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default OrderHistoryOrdersPage;
